// ProjectDetailPage (/projects/:projectId) — sticky identity header (back link,
// icon tile, title, status/priority chips), the six-tab strip, the KPI row, and
// the Overview body (project information, description, members, activity).
//
// LIVE tabs: Overview, Inspections (the read-only project-scoped list in
// ProjectInspections), Activity (the load-more feed in ProjectActivity) and
// Settings. Tasks and Tickets stay coming-soon — neither has an endpoint.
//
// Everything rendered comes from GET /projects/detail/:id, except the Activity
// card (ProjectActivityCard) and tab (ProjectActivity), which fetch the
// project-scoped activity-log feed via the `projectIds` body param on
// POST activity-logs/list. The figures
// the detail endpoint can't answer — Tickets, the KPI sub-metrics/deltas and
// member job titles — render as explicit empty states rather than invented
// numbers; they arrive with their own endpoints.
//
// The two system defaults render reduced variants keyed on `project.type`:
// PUBLIC — everyone in the company is implicitly a member, so there is no
// Settings tab, no information card, and the members card carries a static
// company-wide note instead of a list. TEST — no information card either, and
// Settings holds only the Members panel (see ProjectSettings); everything else
// matches 'private', which is the full page.

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Card, Chip, Icon, IconButton, StatCard, Tabs } from '@/components/common';
import { useGetProjectDetailQuery } from '@/api/endpoints/projectsApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import { PRIORITY_META, STATUS_META, displayStatus } from './projectColumns';
import { fmtDate } from '@/utils/formatDate';
import ProjectActivity from './ProjectActivity';
import ProjectActivityCard from './ProjectActivityCard';
import ProjectInspections from './ProjectInspections';
import ProjectSettings from './ProjectSettings';

type DetailTab = 'overview' | 'inspections' | 'tasks' | 'tickets' | 'activity' | 'settings';

const ProjectDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId = '' } = useParams();
  const [tab, setTab] = useState<DetailTab>('overview');

  const {
    data: project,
    isLoading,
    isError,
  } = useGetProjectDetailQuery(projectId, {
    skip: !projectId,
  });

  const back = (
    <div className='project-detail__back'>
      <IconButton
        icon='arrow-left'
        variant='ghost'
        aria-label={t('projects_detail_back')}
        onClick={() => navigate('/projects')}
      />
      <button
        type='button'
        className='project-detail__back-text'
        onClick={() => navigate('/projects')}
      >
        {t('projects_detail_back')}
      </button>
    </div>
  );

  if (isLoading)
    return (
      <div className='project-detail'>
        {back}
        <Card padded={false} className='project-detail__state'>
          {t('text_loading')}
        </Card>
      </div>
    );

  if (isError || !project)
    return (
      <div className='project-detail'>
        {back}
        <Card padded={false} className='project-detail__state'>
          <Icon name='circle-alert' size={26} />
          {isError ? t('error_generic') : t('projects_detail_not_found')}
        </Card>
      </div>
    );

  const statusValue = displayStatus(project);
  const status = STATUS_META[statusValue];
  const priority = PRIORITY_META[project.priority];

  // The Settings tab doesn't exist on the public project; if the selected tab
  // stops being available (same fallback idea as ProjectSettings' activePanel),
  // Overview takes over rather than stranding the page on a hidden tab.
  const isPublic = project.type === 'public';
  const isTest = project.type === 'test';
  const activeTab: DetailTab = isPublic && tab === 'settings' ? 'overview' : tab;

  // Tickets has no endpoint — the card shows a dash, and the tab carries no badge.
  const stats = [
    {
      key: 'inspections',
      icon: 'clipboard-check',
      tone: 'info' as const,
      value: project.inspectionCount,
    },
    { key: 'tasks', icon: 'square-check', tone: 'success' as const, value: project.taskCount },
    { key: 'tickets', icon: 'ticket', tone: 'warning' as const, value: '—' },
    { key: 'members', icon: 'users', tone: 'brand' as const, value: project.members.length },
  ];

  const infoRows = [
    {
      key: 'status',
      label: t('projects_col_status'),
      value: (
        <Chip variant={status.chip} iconStart={status.icon}>
          {t(`projects_status_${statusValue}`)}
        </Chip>
      ),
    },
    {
      key: 'priority',
      label: t('projects_col_priority'),
      value: (
        <Chip variant={priority.chip} iconStart={priority.icon}>
          {t(`projects_priority_${project.priority}`)}
        </Chip>
      ),
    },
    {
      key: 'owner',
      label: t('projects_col_owner'),
      value: (
        <span className='project-detail__owner'>
          <Avatar
            initials={initialsOf(project.owner.name)}
            tone={toneOf(project.owner.name)}
            size={30}
          />
          {project.owner.name || '—'}
        </span>
      ),
    },
    {
      key: 'start',
      label: t('projects_col_start'),
      value: (
        <span className='project-detail__date'>
          <Icon name='calendar' size={14} />
          {fmtDate(project.startDate)}
        </span>
      ),
    },
    {
      key: 'end',
      label: t('projects_col_end'),
      value: (
        <span className='project-detail__date'>
          <Icon name='calendar' size={14} />
          {fmtDate(project.endDate)}
        </span>
      ),
    },
    {
      key: 'tags',
      label: t('projects_col_tags'),
      value:
        project.tags.length === 0 ? (
          <span className='project-detail__dash'>—</span>
        ) : (
          <span className='project-detail__tags'>
            {project.tags.map((tag) => (
              <Chip key={tag.id} variant='neutral' iconStart='tag'>
                {tag.title}
              </Chip>
            ))}
          </span>
        ),
    },
  ];

  return (
    <div className='project-detail'>
      {/* Header + tabs stay pinned while the body scrolls (see project-detail.scss). */}
      <div className='project-detail__sticky'>
        <Card className='project-detail__header' padded={false}>
          <div className='project-detail__header-body'>
            {back}
            <div className='project-detail__identity'>
              <span className='project-detail__tile'>
                <Icon name={project.icon} size={26} strokeWidth={2} />
              </span>
              <div className='project-detail__headings'>
                <h1 className='project-detail__title'>{project.name}</h1>
                <div className='project-detail__meta'>
                  <Chip variant='neutral' iconStart='hash'>
                    {project.referenceNumber}
                  </Chip>
                  <Chip variant={status.chip} iconStart={status.icon}>
                    {t(`projects_status_${statusValue}`)}
                  </Chip>
                  <Chip variant={priority.chip} iconStart={priority.icon}>
                    {t(`projects_priority_${project.priority}`)}
                  </Chip>
                </div>
              </div>
            </div>
          </div>

          <div className='project-detail__tabs'>
            <Tabs
              aria-label={project.name}
              value={activeTab}
              onChange={(value) => setTab(value as DetailTab)}
              items={[
                { value: 'overview', label: t('projects_tab_overview'), icon: 'layout-grid' },
                {
                  value: 'inspections',
                  label: t('projects_tab_inspections'),
                  icon: 'clipboard-check',
                },
                {
                  value: 'tasks',
                  label: t('projects_tab_tasks'),
                  icon: 'square-check',
                  count: project.taskCount,
                },
                { value: 'tickets', label: t('projects_tab_tickets'), icon: 'ticket' },
                { value: 'activity', label: t('projects_tab_activity'), icon: 'activity' },
                ...(isPublic
                  ? []
                  : [{ value: 'settings', label: t('projects_tab_settings'), icon: 'settings' }]),
              ]}
            />
          </div>
        </Card>
      </div>

      {activeTab === 'settings' ? (
        <ProjectSettings project={project} />
      ) : activeTab === 'inspections' ? (
        <ProjectInspections projectId={project.id} />
      ) : activeTab === 'activity' ? (
        <ProjectActivity projectId={project.id} />
      ) : activeTab !== 'overview' ? (
        <Card padded={false} className='project-detail__state'>
          {t('text_coming_soon')}
        </Card>
      ) : (
        <>
          <div className='project-detail__stats'>
            {stats.map((stat) => (
              <StatCard
                key={stat.key}
                icon={stat.icon}
                tone={stat.tone}
                value={stat.value}
                label={t(`projects_stat_${stat.key}`)}
              />
            ))}
          </div>

          <div className='project-detail__grid'>
            <div className='project-detail__column'>
              {/* Both system defaults drop the information card per the design. */}
              {!isPublic && !isTest && (
                <Card
                  padded={false}
                  head={
                    <span className='project-detail__section-title'>
                      <Icon name='info' size={15} />
                      {t('projects_detail_information')}
                    </span>
                  }
                >
                  <dl className='project-detail__dl'>
                    {infoRows.map((row) => (
                      <div key={row.key} className='project-detail__field'>
                        <dt className='project-detail__label'>{row.label}</dt>
                        <dd className='project-detail__value'>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className='project-detail__description'>
                    <h2 className='project-detail__description-title'>
                      {t('projects_detail_description')}
                    </h2>
                    <p className='project-detail__description-text'>
                      {project.description.trim() || t('projects_detail_no_description')}
                    </p>
                  </div>
                </Card>
              )}

              <Card
                padded={false}
                head={
                  <span className='project-detail__section-title'>
                    <Icon name='users' size={15} />
                    {t('projects_detail_members')}
                    {!isPublic && (
                      <b className='project-detail__section-count'>{project.members.length}</b>
                    )}
                  </span>
                }
              >
                {isPublic ? (
                  // Membership is company-wide by definition — a note, not a list.
                  <div className='project-detail__everyone'>
                    <span className='project-detail__everyone-tile'>
                      <Icon name='globe' size={18} />
                    </span>
                    <span className='project-detail__everyone-text'>
                      <b className='project-detail__everyone-title'>
                        {t('projects_detail_everyone_title')}
                      </b>
                      <span className='project-detail__everyone-body'>
                        {t('projects_detail_everyone_body')}
                      </span>
                    </span>
                  </div>
                ) : project.members.length === 0 ? (
                  <p className='project-detail__empty'>{t('projects_detail_no_members')}</p>
                ) : (
                  <ul className='project-detail__members'>
                    {project.members.map((member) => (
                      <li key={member.id} className='project-detail__member'>
                        <Avatar
                          initials={initialsOf(member.name)}
                          tone={toneOf(member.name)}
                          size={34}
                        />
                        <span className='project-detail__member-name'>{member.name || '—'}</span>
                        {member.id === project.owner.id && (
                          <span className='project-detail__owner-badge'>
                            <Icon name='crown' size={14} />
                            {t('projects_detail_owner_badge')}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            <div className='project-detail__column'>
              <ProjectActivityCard projectId={project.id} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectDetailPage;
